import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../api/axiosConfig";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import {
  ArrowLeft, Image, Settings, Trophy, Smartphone, MapPin, LocateFixed,
  Building2, PackageSearch, Search, Check, ChevronDown, ChevronUp, Loader2,
  X, Plus, FileText, Eye, Palette, Star, Flame, Globe, Tag, SortAsc,
  Sliders, AlignLeft, AlignCenter, AlignRight, Type, LayoutGrid, Trash2, Award, Monitor, Lock,
  ShoppingBag,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:       "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:     "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

function LocationPicker({ lat, lng, onSelect }) {
  useMapEvents({ click(e) { onSelect(e.latlng.lat.toFixed(6), e.latlng.lng.toFixed(6)); } });
  return lat && lng ? <Marker position={[parseFloat(lat), parseFloat(lng)]} /> : null;
}
function MapFlyTo({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) map.flyTo([parseFloat(lat), parseFloat(lng)], 15, { duration: 1 });
  }, [lat, lng, map]);
  return null;
}

const RANK_MEDALS = ["🥇", "🥈", "🥉", "4️⃣", "5️⃣"];
const API_IMG_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/api\/?$/, "");

const FONT_OPTIONS = [
  { val: "sans",    label: "Sans",    family: "system-ui, sans-serif" },
  { val: "serif",   label: "Serif",   family: "Georgia, serif" },
  { val: "mono",    label: "Mono",    family: "'Courier New', monospace" },
  { val: "display", label: "Display", family: "Impact, 'Arial Black', sans-serif" },
];
const FONT_FAMILIES = {
  sans:    "system-ui, -apple-system, sans-serif",
  serif:   "Georgia, 'Times New Roman', serif",
  mono:    "'Courier New', Courier, monospace",
  display: "Impact, 'Arial Black', sans-serif",
};
const FONT_SIZES = { xl: "1.25rem", "2xl": "1.5rem", "3xl": "1.875rem", "4xl": "2.25rem", "5xl": "3rem" };
const TIER_SIZES = { gold: 96, silver: 64, bronze: 44 };

function imgSrc(path) {
  if (!path) return null;
  if (path.startsWith("data:") || path.startsWith("http")) return path;
  return `${API_IMG_BASE}/${path.replace(/^\//, "")}`;
}

function Toggle({ value, onChange, colorOn = "bg-supernova-500" }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? colorOn : "bg-gray-300"}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${value ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

// â”€â”€ Selector de tiendas â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function CompanyPicker({ companies, selected, onToggle, loadingProducts }) {
  const [search,        setSearch]        = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = usar lista base
  const [searching,     setSearching]     = useState(false);
  const timer = useRef(null);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(timer.current);
    if (!val.trim()) { setSearchResults(null); return; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await API.get(`/companies?search=${encodeURIComponent(val)}&limit=50`);
        setSearchResults(res.data?.data?.data ?? res.data?.data ?? []);
      } catch { /* silently ignore */ }
      finally { setSearching(false); }
    }, 350);
  };

  // Cuando hay búsqueda activa → resultados del backend; si no → lista base
  const displayed = searchResults !== null ? searchResults : companies;

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          placeholder="Buscar tienda… (busca en todas las tiendas)"
          value={search}
          onChange={e => handleSearch(e.target.value)}
          className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
        />
        {searching && <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
        {displayed.map(c => {
          const isSelected = selected.includes(c.id);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onToggle(c.id)}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                isSelected
                  ? "border-supernova-400 bg-supernova-50"
                  : "border-gray-100 bg-white hover:border-gray-300"
              }`}
            >
              <div className="relative shrink-0">
                {c.logo || c.logoBase64 ? (
                  <img src={imgSrc(c.logoBase64 || c.logo)} alt={c.name}
                    className="w-10 h-10 rounded-full object-cover border border-gray-200" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <Building2 size={18} className="text-gray-400" />
                  </div>
                )}
                {isSelected && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-supernova-500 rounded-full flex items-center justify-center">
                    <Check size={10} className="text-white" />
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{c.name}</p>
                {c.status && c.status !== "Activo" && (
                  <p className="text-xs text-orange-400 font-medium">{c.status}</p>
                )}
                {loadingProducts[c.id] && (
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <Loader2 size={10} className="animate-spin" /> Cargando…
                  </p>
                )}
              </div>
            </button>
          );
        })}
        {displayed.length === 0 && !searching && (
          <p className="col-span-2 text-center text-gray-400 text-sm py-4">
            {search ? "Sin resultados para esa búsqueda" : "No hay tiendas cargadas"}
          </p>
        )}
      </div>
      <p className="text-xs text-gray-400">
        {searchResults !== null
          ? `${displayed.length} resultado${displayed.length !== 1 ? "s" : ""} en todas las tiendas`
          : `Mostrando ${displayed.length} tiendas — escribe para buscar en todas`}
      </p>
    </div>
  );
}

// â”€â”€ Sección de productos por tienda â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function SponsorProductSection({ sponsor, company, products, onToggleProduct, onRemoveSponsor, onUpdateDisplay }) {
  const [open, setOpen] = useState(true);
  const [displayOpen, setDisplayOpen] = useState(false);
  const logo = imgSrc(company?.logoBase64 || company?.logo);

  const handleSponsorLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => onUpdateDisplay({ sponsorLogoBase64: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
        <button type="button" onClick={() => setOpen(v => !v)}
          className="flex items-center gap-3 flex-1 text-left">
          {logo
            ? <img src={logo} alt={company?.name} className="w-8 h-8 rounded-full object-cover border border-gray-200" />
            : <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center"><Building2 size={15} className="text-gray-500" /></div>
          }
          <div>
            <p className="font-semibold text-gray-800 text-sm">{company?.name}</p>
            <p className="text-xs text-gray-500">
              {sponsor.products.length} producto{sponsor.products.length !== 1 ? "s" : ""} seleccionado{sponsor.products.length !== 1 ? "s" : ""}
            </p>
          </div>
          <span className="ml-auto mr-2 text-gray-400">
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </button>
        <button type="button" onClick={onRemoveSponsor}
          className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition">
          <X size={16} />
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-4">
              {/* Productos */}
              {products.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-3">Esta tienda no tiene productos activos.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {products.map(p => {
                    const selected = sponsor.products.includes(p.id);
                    const thumb = imgSrc(p.imageBase64 || (p.images?.[0]?.imageBase64) || p.imageUrl || (p.images?.[0]?.url));
                    return (
                      <button key={p.id} type="button" onClick={() => onToggleProduct(p.id)}
                        className={`relative rounded-xl overflow-hidden border-2 transition-all text-left ${
                          selected ? "border-supernova-500 ring-2 ring-supernova-300" : "border-gray-100 hover:border-gray-300"
                        }`}
                      >
                        <div className="h-20 bg-gray-100 overflow-hidden">
                          {thumb
                            ? <img src={thumb} alt={p.title} className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center"><PackageSearch size={22} className="text-gray-300" /></div>
                          }
                        </div>
                        <div className="p-1.5">
                          <p className="text-xs font-medium text-gray-700 line-clamp-2 leading-tight">{p.title}</p>
                        </div>
                        {selected && (
                          <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-supernova-500 rounded-full flex items-center justify-center shadow">
                            <Check size={11} className="text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Display settings */}
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <button type="button"
                  onClick={() => setDisplayOpen(v => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition text-left text-xs font-semibold text-gray-600"
                >
                  <Palette size={12} className="text-supernova-500" />
                  Configuración de visualización
                  <span className="ml-auto">{displayOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</span>
                </button>
                <AnimatePresence initial={false}>
                  {displayOpen && (
                    <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
                      <div className="p-3 space-y-3">
                        {/* Tipo */}
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block font-medium">Tipo de participante</label>
                          <div className="flex gap-2">
                            {[{ val: "brand", label: "Patrocinador" }, { val: "participant", label: "Participante" }].map(opt => (
                              <button key={opt.val} type="button"
                                onClick={() => onUpdateDisplay({ sponsorType: opt.val })}
                                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border-2 transition ${
                                  (sponsor.sponsorType ?? "participant") === opt.val
                                    ? "border-supernova-500 bg-supernova-50 text-supernova-700"
                                    : "border-gray-200 text-gray-500 hover:border-gray-300"
                                }`}
                              >{opt.label}</button>
                            ))}
                          </div>
                        </div>
                        {/* Nombre de display */}
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block font-medium flex items-center gap-1"><Tag size={11} /> Nombre en la app (opcional)</label>
                          <input
                            placeholder={company?.name}
                            value={sponsor.displayName ?? ""}
                            onChange={e => onUpdateDisplay({ displayName: e.target.value || null })}
                            className="w-full p-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-supernova-400"
                          />
                        </div>
                        {/* Sitio web */}
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block font-medium flex items-center gap-1"><Globe size={11} /> Sitio web</label>
                          <input
                            placeholder="https://..."
                            value={sponsor.websiteUrl ?? ""}
                            onChange={e => onUpdateDisplay({ websiteUrl: e.target.value || null })}
                            className="w-full p-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-supernova-400"
                          />
                        </div>
                        {/* Orden */}
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block font-medium flex items-center gap-1"><SortAsc size={11} /> Orden de aparición</label>
                          <input
                            type="number" min="0" value={sponsor.displayOrder ?? 0}
                            onChange={e => onUpdateDisplay({ displayOrder: Number(e.target.value) })}
                            className="w-24 p-2 border border-gray-200 rounded-lg text-xs font-bold text-center focus:outline-none focus:ring-2 focus:ring-supernova-400"
                          />
                        </div>
                        {/* Membresía */}
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block font-medium flex items-center gap-1">
                            <Award size={11} className="text-amber-500" /> Membresía / Visibilidad
                          </label>
                          <div className="flex gap-1.5">
                            {[
                              { val: "gold",   label: "🥇 Oro",    active: "border-amber-400 bg-amber-50 text-amber-700" },
                              { val: "silver", label: "🥈 Plata",  active: "border-gray-400 bg-gray-100 text-gray-700" },
                              { val: "bronze", label: "🥉 Bronce", active: "border-orange-400 bg-orange-50 text-orange-700" },
                            ].map(tier => (
                              <button key={tier.val} type="button"
                                onClick={() => onUpdateDisplay({ membershipTier: tier.val })}
                                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border-2 transition ${
                                  (sponsor.membershipTier ?? "bronze") === tier.val
                                    ? tier.active
                                    : "border-gray-200 text-gray-500 hover:border-gray-300"
                                }`}
                              >{tier.label}</button>
                            ))}
                          </div>
                          <p className="text-xs text-gray-400 mt-1">Oro = logos grandes · Plata = medianos · Bronce = pequeños</p>
                        </div>

                        {/* Logo propio */}
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block font-medium">Logo propio (sobreescribe logo de empresa)</label>
                          <div className="flex items-center gap-3">
                            {sponsor.sponsorLogoBase64 && (
                              <div className="relative shrink-0">
                                <img src={sponsor.sponsorLogoBase64} alt="logo" className="w-12 h-12 rounded-full object-cover border border-gray-200" />
                                <button type="button"
                                  onClick={() => onUpdateDisplay({ sponsorLogoBase64: null })}
                                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-white">
                                  <X size={9} />
                                </button>
                              </div>
                            )}
                            <label className="flex items-center gap-1.5 cursor-pointer border border-dashed border-gray-300 hover:border-supernova-400 rounded-lg px-3 py-1.5 text-xs text-gray-500 transition">
                              <Plus size={11} /> Subir logo
                              <input type="file" accept="image/*" onChange={handleSponsorLogoUpload} className="hidden" />
                            </label>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// â”€â”€ Vista previa del evento â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function EventPreview({ form, companies }) {
  const bgStyle = (() => {
    if (form.backgroundType === "gradient") {
      return { background: `linear-gradient(${form.backgroundGradientAngle}deg, ${form.backgroundGradientFrom}, ${form.backgroundGradientTo})` };
    }
    if (form.backgroundType === "image" && form.backgroundImageBase64) {
      return {
        backgroundImage: `url(${imgSrc(form.backgroundImageBase64)})`,
        backgroundSize: form.backgroundImageSize,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    }
    return { backgroundColor: form.backgroundColor };
  })();

  const titleStyle = {
    color: form.titleColor,
    fontFamily: FONT_FAMILIES[form.titleFont],
    fontSize: FONT_SIZES[form.titleFontSize],
    textAlign: form.titleAlign,
    fontWeight: "bold",
    lineHeight: 1.2,
  };

  function SponsorLogo({ sponsor, size }) {
    const company = companies.find(c => c.id === sponsor.company);
    const src = imgSrc(sponsor.sponsorLogoBase64 || company?.logoBase64 || company?.logo);
    if (!src) return (
      <div style={{ width: size, height: size }}
        className="rounded-full bg-white/20 border border-white/30 flex items-center justify-center shrink-0">
        <Building2 size={size * 0.4} className="text-white/60" />
      </div>
    );
    return (
      <div style={{ width: size, height: size }}
        className="rounded-full overflow-hidden border border-white/30 bg-white shrink-0">
        <img src={src} alt="" className="w-full h-full object-contain p-0.5" />
      </div>
    );
  }

  const allSponsors = [...form.sponsors].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  const goldSponsors   = allSponsors.filter(s => s.membershipTier === "gold");
  const silverSponsors = allSponsors.filter(s => s.membershipTier === "silver");
  const bronzeSponsors = allSponsors.filter(s => s.membershipTier === "bronze" || !s.membershipTier);
  const scale = 0.45;

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-3 sticky top-20 z-30">
      <h2 className="font-black text-gray-800 flex items-center gap-2 text-sm">
        <Monitor size={16} className="text-supernova-500" /> Vista previa
      </h2>
      <div className="rounded-xl overflow-hidden" style={{ minHeight: 160, ...bgStyle }}>
        {/* Banner */}
        {form.bannerBase64 && (
          <div className="w-full h-20 overflow-hidden">
            <img src={imgSrc(form.bannerBase64)} alt="banner" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-3">
          {/* Logo */}
          {form.eventLogoBase64 && (
            <div className="flex justify-center mb-2">
              <img src={imgSrc(form.eventLogoBase64)} alt="logo" className="h-8 object-contain" />
            </div>
          )}
          {/* Title */}
          <p style={{ ...titleStyle, fontSize: `calc(${FONT_SIZES[form.titleFontSize]} * 0.55)` }}
            className="drop-shadow mb-2">
            {form.title || "Título del evento"}
          </p>
          {/* Sponsors preview */}
          {form.showSponsorsSection && allSponsors.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {goldSponsors.length > 0 && (
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {goldSponsors.map(s => <SponsorLogo key={s.company} sponsor={s} size={Math.round(TIER_SIZES.gold * scale)} />)}
                </div>
              )}
              {silverSponsors.length > 0 && (
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {silverSponsors.map(s => <SponsorLogo key={s.company} sponsor={s} size={Math.round(TIER_SIZES.silver * scale)} />)}
                </div>
              )}
              {bronzeSponsors.length > 0 && (
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {bronzeSponsors.map(s => <SponsorLogo key={s.company} sponsor={s} size={Math.round(TIER_SIZES.bronze * scale)} />)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-400 text-center">Vista previa aproximada</p>
    </div>
  );
}

// â”€â”€ Editor de Términos y Condiciones â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const DEFAULT_TERMS = `TÉRMINOS Y CONDICIONES DE PARTICIPACIÓN

1. ACEPTACIÓN
Al registrarse en este evento, el participante acepta íntegramente los presentes Términos y Condiciones.

2. REQUISITOS DE PARTICIPACIÓN
- El participante debe ser mayor de edad o contar con autorización de su representante legal.
- Los productos presentados deben cumplir con las normas sanitarias y de calidad vigentes.
- Cada participante es responsable de la calidad e inocuidad de sus productos.

3. PROPIEDAD INTELECTUAL
Los participantes conservan los derechos sobre sus recetas y marcas. Al participar autorizan el uso de su nombre comercial y fotografías para la difusión del evento.

4. RESPONSABILIDAD
El organizador no se hace responsable por daños, pérdidas o accidentes ocurridos durante el evento que sean atribuibles al participante o a terceros.

5. PRIVACIDAD
Los datos personales recopilados serán utilizados únicamente para la gestión del evento y no serán compartidos con terceros sin consentimiento previo.

6. MODIFICACIONES
El organizador se reserva el derecho de modificar estos términos con previo aviso a los participantes.

Al aceptar, declaro haber leído y comprendido los presentes términos y condiciones.`;

function TermsEditor({ value, onChange }) {
  const [preview, setPreview] = useState(false);
  const hasContent = value?.trim().length > 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-black text-gray-800 flex items-center gap-2">
          <FileText size={18} className="text-supernova-500" /> Términos y Condiciones
        </h2>
        <div className="flex items-center gap-2">
          {hasContent && (
            <button type="button" onClick={() => setPreview(v => !v)}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition ${
                preview ? "bg-supernova-100 text-supernova-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              <Eye size={13} /> {preview ? "Editar" : "Vista previa"}
            </button>
          )}
          {!hasContent && (
            <button type="button"
              onClick={() => onChange(DEFAULT_TERMS)}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-supernova-50 text-supernova-700 hover:bg-supernova-100 transition">
              <Plus size={13} /> Usar plantilla
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-400 leading-relaxed">
        Los participantes deberán aceptar estos términos al registrarse. Puedes enviarlos también a los participantes ya inscritos desde la página del evento.
      </p>

      {preview ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-72 overflow-y-auto">
          <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{value}</pre>
        </div>
      ) : (
        <textarea
          placeholder="Escribe aquí los términos y condiciones del evento…&#10;&#10;Puedes usar el botón 'Usar plantilla' para partir de un ejemplo."
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={10}
          className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 resize-y font-mono leading-relaxed"
        />
      )}

      {hasContent && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-400">{value.length} caracteres</p>
          <button type="button" onClick={() => { onChange(""); setPreview(false); }}
            className="text-xs text-red-400 hover:text-red-600 transition flex items-center gap-1">
            <X size={11} /> Limpiar
          </button>
        </div>
      )}
    </div>
  );
}

// â”€â”€ Página principal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const EMPTY_FORM = {
  title: "", description: "", startDate: "", endDate: "",
  bannerBase64: "", sponsors: [],
  maxVotesPerDevice: 1, votingPoints: [1], publishResults: false,
  requireDeviceId: true, isOpenEvent: false,
  canRemoveVote: true,
  canModifyVote: false,
  votingClosed: false,
  requireLocation: false, locationLat: "", locationLng: "", locationRadiusMeters: 500,
  termsAndConditions: "",
  votingType: "simple",
  voteButtonLabel: "¡Votar!",
  templateId: "default",
  showSponsorsSection: false,
  eventLogoBase64: "",
  // Diseño del evento
  backgroundType: "color",
  backgroundColor: "#f9fafb",
  backgroundGradientFrom: "#1e293b",
  backgroundGradientTo: "#7c3aed",
  backgroundGradientAngle: 135,
  backgroundImageBase64: "",
  backgroundImageSize: "cover",
  titleColor: "#111827",
  titleFont: "sans",
  titleFontSize: "3xl",
  titleAlign: "center",
  sponsorsLayout: "static",
  sponsorsPerRow: 4,
  // Botón productos participantes en landing
  showProductsButton: false,
  productsButtonLabel: "Ver productos participantes",
};

function buildDefaultPoints(max) {
  return Array.from({ length: max }, (_, i) => max - i);
}

export default function EventForm() {
  const { id } = useParams();          // undefined = new event
  const navigate  = useNavigate();
  const isEditing = Boolean(id);

  const [form, setForm]             = useState(EMPTY_FORM);
  const [bannerPreview, setBanner]  = useState("");
  const [companies, setCompanies]   = useState([]);
  const [productsByCompany, setProds] = useState({});
  const [loadingProds, setLoadingProds] = useState({});
  const [saving, setSaving]         = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(isEditing);
  const [logoPreview, setLogoPreview]   = useState("");
  const [bgPreview, setBgPreview]       = useState("");

  // â”€â”€ Load companies once â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    API.get("/companies?limit=500")
      .then(res => setCompanies(res.data?.data?.data ?? res.data?.data ?? []))
      .catch(() => toast.error("Error cargando tiendas"));
  }, []);

  // â”€â”€ Load event when editing â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!isEditing) return;
    API.get(`/events/${id}`)
      .then(res => {
        const ev = res.data?.data?.event ?? res.data?.data ?? res.data;
        if (!ev) return;
        const maxV = ev.maxVotesPerDevice ?? 1;
        // Normalize sponsors: flatten products to array of IDs + include display fields
        const sponsors = (ev.sponsors || []).map(s => ({
          company:           s.company?.id ?? s.company,
          products:          (s.products || []).map(p => p.productId ?? p.product?.id ?? p.id ?? p),
          displayName:       s.displayName ?? null,
          sponsorLogoBase64: s.sponsorLogoBase64 ?? null,
          websiteUrl:        s.websiteUrl ?? null,
          displayOrder:      s.displayOrder ?? 0,
          sponsorType:       s.sponsorType ?? "participant",
          membershipTier:    s.membershipTier ?? "bronze",
        }));
        setForm({
          title:                    ev.title ?? "",
          description:              ev.description ?? "",
          startDate:                ev.startDate?.substring(0, 10) ?? "",
          endDate:                  ev.endDate?.substring(0, 10) ?? "",
          bannerBase64:             ev.bannerBase64 ?? "",
          sponsors,
          maxVotesPerDevice:        maxV,
          votingPoints:             Array.isArray(ev.votingPoints) ? ev.votingPoints : buildDefaultPoints(maxV),
          publishResults:           ev.publishResults ?? false,
          requireDeviceId:          ev.requireDeviceId ?? true,
          isOpenEvent:              ev.isOpenEvent ?? false,
          canRemoveVote:            ev.canRemoveVote ?? true,
          canModifyVote:            ev.canModifyVote ?? false,
          votingClosed:             ev.votingClosed ?? false,
          requireLocation:          ev.requireLocation ?? false,
          locationLat:              ev.locationLat ?? "",
          locationLng:              ev.locationLng ?? "",
          locationRadiusMeters:     ev.locationRadiusMeters ?? 500,
          termsAndConditions:       ev.termsAndConditions ?? "",
          votingType:               ev.votingType ?? "simple",
          voteButtonLabel:          ev.voteButtonLabel ?? "¡Votar!",
          templateId:               ev.templateId ?? "default",
          showSponsorsSection:      ev.showSponsorsSection ?? false,
          eventLogoBase64:          ev.eventLogoBase64 ?? "",
          backgroundType:           ev.backgroundType ?? "color",
          backgroundColor:          ev.backgroundColor ?? "#f9fafb",
          backgroundGradientFrom:   ev.backgroundGradientFrom ?? "#1e293b",
          backgroundGradientTo:     ev.backgroundGradientTo ?? "#7c3aed",
          backgroundGradientAngle:  ev.backgroundGradientAngle ?? 135,
          backgroundImageBase64:    ev.backgroundImageBase64 ?? "",
          backgroundImageSize:      ev.backgroundImageSize ?? "cover",
          titleColor:               ev.titleColor ?? "#111827",
          titleFont:                ev.titleFont ?? "sans",
          titleFontSize:            ev.titleFontSize ?? "3xl",
          titleAlign:               ev.titleAlign ?? "center",
          sponsorsLayout:           ev.sponsorsLayout ?? "static",
          sponsorsPerRow:           ev.sponsorsPerRow ?? 4,
          showProductsButton:       ev.showProductsButton ?? false,
          productsButtonLabel:      ev.productsButtonLabel ?? "Ver productos participantes",
        });
        setBanner(imgSrc(ev.bannerBase64) || "");
        setLogoPreview(imgSrc(ev.eventLogoBase64) || "");
        setBgPreview(imgSrc(ev.backgroundImageBase64) || "");
        // Pre-load products for each sponsor
        sponsors.forEach(s => loadProducts(s.company));
      })
      .catch(() => toast.error("Error cargando evento"))
      .finally(() => setLoadingEvent(false));
  // eslint-disable-next-line
  }, [id]);

  const loadProducts = async (companyId) => {
    if (!companyId || productsByCompany[companyId]) return;
    setLoadingProds(prev => ({ ...prev, [companyId]: true }));
    try {
      const res = await API.get(`/products/company/${companyId}`);
      const list = res.data?.data?.products ?? res.data?.data ?? [];
      setProds(prev => ({ ...prev, [companyId]: list }));
    } catch { /* silent */ }
    finally { setLoadingProds(prev => ({ ...prev, [companyId]: false })); }
  };

  // â”€â”€ Company toggle â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const toggleCompany = (companyId) => {
    setForm(prev => {
      const exists = prev.sponsors.find(s => s.company === companyId);
      if (exists) {
        return { ...prev, sponsors: prev.sponsors.filter(s => s.company !== companyId) };
      }
      loadProducts(companyId);
      return { ...prev, sponsors: [...prev.sponsors, {
        company: companyId, products: [],
        displayName: null, sponsorLogoBase64: null, websiteUrl: null, displayOrder: prev.sponsors.length, sponsorType: "participant", membershipTier: "bronze",
      }]};
    });
  };

  const updateSponsorDisplay = (companyId, patch) => {
    setForm(prev => ({
      ...prev,
      sponsors: prev.sponsors.map(s => s.company === companyId ? { ...s, ...patch } : s),
    }));
  };

  const removeSponsor = (companyId) => {
    setForm(prev => ({ ...prev, sponsors: prev.sponsors.filter(s => s.company !== companyId) }));
  };

  // â”€â”€ Product toggle â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const toggleProduct = (companyId, productId) => {
    setForm(prev => ({
      ...prev,
      sponsors: prev.sponsors.map(s => {
        if (s.company !== companyId) return s;
        const products = s.products.includes(productId)
          ? s.products.filter(id => id !== productId)
          : [...s.products, productId];
        return { ...s, products };
      }),
    }));
  };

  // â”€â”€ Votes â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleMaxVotesChange = (val) => {
    const isPoints = form.votingType === "points";
    const cap = isPoints ? 5 : 999;
    const max = Math.max(1, Math.min(cap, Number(val) || 1));
    setForm(prev => ({
      ...prev,
      maxVotesPerDevice: max,
      // En modo puntos la tabla de puntos depende del número de votos; en estrellas no se usa
      votingPoints: isPoints ? buildDefaultPoints(max) : prev.votingPoints,
    }));
  };

  // â”€â”€ Banner â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBanner(URL.createObjectURL(file));
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await API.post("/upload/event-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filePath = res.data?.data?.path || res.data?.data?.url;
      setForm(prev => ({ ...prev, bannerBase64: filePath }));
    } catch {
      toast.error("Error al subir la imagen");
      setBanner("");
      setForm(prev => ({ ...prev, bannerBase64: "" }));
    }
  };

  // â”€â”€ Event logo (template) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleEventLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await API.post("/upload/event-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filePath = res.data?.data?.path || res.data?.data?.url;
      setForm(prev => ({ ...prev, eventLogoBase64: filePath }));
    } catch {
      toast.error("Error al subir el logo");
      setLogoPreview("");
      setForm(prev => ({ ...prev, eventLogoBase64: "" }));
    }
  };

  // â”€â”€ Background image â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleBackgroundImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBgPreview(URL.createObjectURL(file));
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await API.post("/upload/event-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filePath = res.data?.data?.path || res.data?.data?.url;
      setForm(prev => ({ ...prev, backgroundImageBase64: filePath }));
    } catch {
      toast.error("Error al subir la imagen de fondo");
      setBgPreview("");
      setForm(prev => ({ ...prev, backgroundImageBase64: "" }));
    }
  };

  // â”€â”€ GPS â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleGetMyLocation = () => {
    navigator.geolocation?.getCurrentPosition(
      pos => setForm(prev => ({
        ...prev,
        locationLat: pos.coords.latitude.toFixed(6),
        locationLng: pos.coords.longitude.toFixed(6),
      })),
      () => {},
      { timeout: 8000 },
    );
  };

  // â”€â”€ Submit â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.startDate || !form.endDate) {
      toast.warning("Título y fechas son obligatorios");
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await API.put(`/events/${id}`, form);
        toast.success("Evento actualizado");
      } else {
        await API.post("/events", form);
        toast.success("Evento creado");
      }
      navigate("/events");
    } catch { toast.error("Error al guardar el evento"); }
    finally { setSaving(false); }
  };

  const selectedCompanyIds = form.sponsors.map(s => s.company);

  if (loadingEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-supernova-50 to-supernova-100">
        <Loader2 size={32} className="animate-spin text-supernova-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-supernova-50 to-supernova-100">
      {/* â”€â”€ Sticky header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="sticky top-0 z-30 bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <button onClick={() => navigate("/events")}
            className="flex items-center gap-2 text-gray-600 hover:text-supernova-600 transition text-sm font-medium">
            <ArrowLeft size={18} /> Eventos
          </button>
          <h1 className="font-black text-gray-800 text-base truncate">
            {isEditing ? "Editar evento" : "Nuevo evento"}
          </h1>
          <button type="button" onClick={handleSubmit} disabled={saving}
            className="flex items-center gap-2 bg-supernova-500 hover:bg-supernova-600 text-white px-5 py-2 rounded-xl text-sm font-bold transition disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : null}
            {saving ? "Guardando…" : isEditing ? "Actualizar" : "Crear evento"}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* â”€â”€ Columna izquierda: Info + Config â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <div className="lg:col-span-2 space-y-6 isolate">

            {/* Info básica */}
            <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-4">
              <h2 className="font-black text-gray-800 flex items-center gap-2">
                <Trophy size={18} className="text-supernova-500" /> Información básica
              </h2>
              <input
                placeholder="Título del evento *"
                value={form.title}
                onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                required
              />
              <textarea
                placeholder="Descripción del evento"
                value={form.description}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 resize-none"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block font-medium">Inicio *</label>
                  <input type="date" value={form.startDate}
                    onChange={e => setForm(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                    required />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block font-medium">Fin *</label>
                  <input type="date" value={form.endDate}
                    onChange={e => setForm(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                    required />
                </div>
              </div>
            </div>

            {/* Banner */}
            <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-3">
              <h2 className="font-black text-gray-800 flex items-center gap-2">
                <Image size={18} className="text-supernova-500" /> Banner del evento
              </h2>
              <label className="flex items-center justify-center gap-2 cursor-pointer border-2 border-dashed border-gray-200 hover:border-supernova-400 rounded-xl p-4 transition">
                <Plus size={16} className="text-gray-400" />
                <span className="text-sm text-gray-500">Subir imagen</span>
                <input type="file" accept="image/*" onChange={handleBannerUpload} className="hidden" />
              </label>
              {bannerPreview && (
                <div className="relative">
                  <img src={bannerPreview} alt="preview" className="w-full h-44 object-cover rounded-xl border" />
                  <button type="button"
                    onClick={() => { setForm(prev => ({ ...prev, bannerBase64: "" })); setBanner(""); }}
                    className="absolute top-2 right-2 w-7 h-7 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition">
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>

            {/* Configuración */}
            <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-5">
              <h2 className="font-black text-gray-800 flex items-center gap-2">
                <Settings size={18} className="text-supernova-500" /> Configuración
              </h2>

              {/* Votos / calificaciones por dispositivo */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">
                  {form.votingType === "stars" ? "Productos calificables por persona" : "Votos por dispositivo"}
                </label>

                {/* Modo puntos: solo 1–5 (hay tabla de medallas) */}
                {form.votingType === "points" && (
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button key={n} type="button" onClick={() => handleMaxVotesChange(n)}
                        className={`w-10 h-10 rounded-full font-bold text-sm transition border-2 ${
                          form.maxVotesPerDevice === n
                            ? "bg-supernova-500 text-white border-supernova-500"
                            : "bg-white text-gray-600 border-gray-200 hover:border-supernova-400"
                        }`}
                      >{n}</button>
                    ))}
                  </div>
                )}

                {/* Modo simple y estrellas: accesos rápidos + input libre */}
                {form.votingType !== "points" && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {(form.votingType === "stars" ? [5, 10, 15, 20, 30] : [1, 2, 3, 5, 10]).map(n => (
                        <button key={n} type="button" onClick={() => handleMaxVotesChange(n)}
                          className={`px-3 h-9 rounded-xl font-bold text-sm transition border-2 ${
                            form.maxVotesPerDevice === n
                              ? "bg-supernova-500 text-white border-supernova-500"
                              : "bg-white text-gray-600 border-gray-200 hover:border-supernova-400"
                          }`}
                        >{n}</button>
                      ))}
                      <div className="flex items-center gap-1.5 ml-1">
                        <input
                          type="number" min="1" max="999"
                          value={form.maxVotesPerDevice}
                          onChange={e => handleMaxVotesChange(e.target.value)}
                          className="w-20 p-2 border-2 border-gray-200 rounded-xl text-sm font-bold text-center focus:outline-none focus:border-supernova-400"
                        />
                        <span className="text-xs text-gray-400">máx</span>
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-400 mt-1.5">
                  {form.votingType === "stars"
                    ? `Cada persona puede calificar hasta ${form.maxVotesPerDevice} producto${form.maxVotesPerDevice !== 1 ? "s" : ""}. Ponlo igual o mayor al número de burgers participantes.`
                    : form.maxVotesPerDevice === 1
                      ? "1 voto — el usuario puede cambiarlo antes de que cierre"
                      : `${form.maxVotesPerDevice} votos — uno por producto diferente`}
                </p>

                {/* Alerta específica para estrelllas sin suficientes votos */}
                {form.votingType === "stars" && form.maxVotesPerDevice < 5 && (
                  <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    <Star size={13} className="text-amber-500 fill-amber-400 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      Con {form.maxVotesPerDevice} solo podrán calificar {form.maxVotesPerDevice} burger{form.maxVotesPerDevice !== 1 ? "s" : ""}. Para que todos califiquen todos los productos, auméntalo al total de participantes.
                    </p>
                  </div>
                )}
              </div>


              {/* Toggles */}
              <div className="space-y-3 pt-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                      <Building2 size={14} className="text-purple-500" />
                      {form.isOpenEvent ? "Evento libre (abierto)" : "Evento privado"}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {form.isOpenEvent ? "Cualquier tienda puede unirse automáticamente" : "Admin debe aprobar la participación"}
                    </p>
                  </div>
                  <Toggle value={form.isOpenEvent} onChange={v => setForm(prev => ({ ...prev, isOpenEvent: v }))} colorOn="bg-purple-500" />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                      <Smartphone size={14} className="text-blue-500" /> Validar dispositivo
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">Bloquea votos duplicados por dispositivo</p>
                  </div>
                  <Toggle value={form.requireDeviceId} onChange={v => setForm(prev => ({ ...prev, requireDeviceId: v }))} />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                      <MapPin size={14} className="text-green-600" /> Requerir ubicación
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">Solo votan personas dentro del radio</p>
                  </div>
                  <Toggle value={form.requireLocation} onChange={v => setForm(prev => ({ ...prev, requireLocation: v }))} colorOn="bg-green-500" />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                      <Trash2 size={14} className="text-red-400" /> Permitir eliminar voto
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">El usuario puede retirar su voto antes de que cierre</p>
                  </div>
                  <Toggle value={form.canRemoveVote} onChange={v => setForm(prev => ({ ...prev, canRemoveVote: v }))} colorOn="bg-red-400" />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                      <SortAsc size={14} className="text-blue-500" /> Permitir modificar voto
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {form.votingType === "stars"
                        ? "El usuario puede cambiar su calificación de estrellas"
                        : form.maxVotesPerDevice > 1
                        ? "El usuario puede cambiar la posición de sus votos"
                        : "El usuario puede cambiar por cuál producto vota"}
                    </p>
                  </div>
                  <Toggle value={form.canModifyVote} onChange={v => setForm(prev => ({ ...prev, canModifyVote: v }))} colorOn="bg-blue-500" />
                </div>

                {/* Votación cerrada */}
                <div className={`flex items-center justify-between rounded-xl px-4 py-3 border transition ${form.votingClosed ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"}`}>
                  <div>
                    <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: form.votingClosed ? "#b91c1c" : "#374151" }}>
                      <Lock size={14} className={form.votingClosed ? "text-red-600" : "text-gray-500"} />
                      Votación finalizada
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: form.votingClosed ? "#ef4444" : "#9ca3af" }}>
                      {form.votingClosed
                        ? "â›” Los votos están bloqueados. Nadie puede votar ni modificar su voto."
                        : "La votación está abierta. Activa esto para cerrarla manualmente."}
                    </p>
                  </div>
                  <Toggle value={form.votingClosed} onChange={v => setForm(prev => ({ ...prev, votingClosed: v }))} colorOn="bg-red-600" />
                </div>
              </div>

              {/* Mapa de ubicación */}
              {form.requireLocation && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 space-y-3">
                  <p className="text-xs text-green-700 font-medium">Toca el mapa para fijar la ubicación del evento.</p>
                  <div className="rounded-xl overflow-hidden border border-green-300" style={{ height: 220 }}>
                    <MapContainer
                      center={form.locationLat && form.locationLng
                        ? [parseFloat(form.locationLat), parseFloat(form.locationLng)]
                        : [4.711, -74.072]}
                      zoom={14}
                      style={{ height: "100%", width: "100%" }}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                      <LocationPicker lat={form.locationLat} lng={form.locationLng}
                        onSelect={(lat, lng) => setForm(prev => ({ ...prev, locationLat: lat, locationLng: lng }))} />
                      <MapFlyTo lat={form.locationLat} lng={form.locationLng} />
                    </MapContainer>
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Latitud</label>
                        <input type="number" step="any" placeholder="4.711" value={form.locationLat}
                          onChange={e => setForm(prev => ({ ...prev, locationLat: e.target.value }))}
                          className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Longitud</label>
                        <input type="number" step="any" placeholder="-74.072" value={form.locationLng}
                          onChange={e => setForm(prev => ({ ...prev, locationLng: e.target.value }))}
                          className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400" />
                      </div>
                    </div>
                    <button type="button" onClick={handleGetMyLocation} title="Mi ubicación"
                      className="p-2.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition shrink-0 mb-0.5">
                      <LocateFixed size={16} />
                    </button>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-2 block font-medium">Radio permitido</label>
                    <div className="flex items-center gap-3">
                      <input type="range" min="50" max="50000" step="50"
                        value={Math.min(form.locationRadiusMeters, 50000)}
                        onChange={e => setForm(prev => ({ ...prev, locationRadiusMeters: Number(e.target.value) }))}
                        className="flex-1 accent-supernova-500" />
                      <div className="flex items-center gap-1 shrink-0">
                        <input type="number" min="0.05" step="0.05"
                          value={(form.locationRadiusMeters / 1000).toFixed(2)}
                          onChange={e => {
                            const km = parseFloat(e.target.value);
                            if (!isNaN(km) && km > 0) setForm(prev => ({ ...prev, locationRadiusMeters: Math.round(km * 1000) }));
                          }}
                          className="w-20 p-1.5 border border-gray-200 rounded-lg text-sm text-center font-bold focus:outline-none focus:ring-2 focus:ring-supernova-400" />
                        <span className="text-xs text-gray-500">km</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {form.locationRadiusMeters >= 1000
                        ? `${(form.locationRadiusMeters / 1000).toFixed(1)} km de radio`
                        : `${form.locationRadiusMeters} m de radio`}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Términos y Condiciones */}
            <TermsEditor
              value={form.termsAndConditions}
              onChange={v => setForm(prev => ({ ...prev, termsAndConditions: v }))}
            />

            {/* Apariencia y Plantilla */}
            <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-5">
              <h2 className="font-black text-gray-800 flex items-center gap-2">
                <Palette size={18} className="text-supernova-500" /> Apariencia y Plantilla
              </h2>

              {/* Template selector */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">Plantilla del evento</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: "default",       label: "Default",        desc: "Vista estándar",           icon: <Settings size={20} className="text-gray-400" /> },
                    { id: "burgerfest",    label: "Burger Fest",    desc: "Fondo negro con brasas",   icon: <Flame size={20} className="text-orange-500" /> },
                    { id: "dotburgerfest", label: "Dot Burger Fest", desc: "Festival premium con dots", icon: <span style={{ fontSize: "1.25rem" }}>🍔</span> },
                  ].map(t => (
                    <button key={t.id} type="button"
                      onClick={() => setForm(prev => ({ ...prev, templateId: t.id }))}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition ${
                        form.templateId === t.id
                          ? "border-supernova-500 bg-supernova-50"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      {t.icon}
                      <span className="text-sm font-bold text-gray-800">{t.label}</span>
                      <span className="text-xs text-gray-400 text-center">{t.desc}</span>
                      {form.templateId === t.id && (
                        <span className="w-5 h-5 bg-supernova-500 rounded-full flex items-center justify-center">
                          <Check size={11} className="text-white" />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Logo del evento */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2 flex items-center gap-1.5">
                  <Image size={14} className="text-supernova-500" /> Logo del evento
                </label>
                <div className="flex items-center gap-4">
                  {(logoPreview || form.eventLogoBase64) && (
                    <div className="relative shrink-0">
                      <img src={logoPreview || imgSrc(form.eventLogoBase64)} alt="event logo" className="w-20 h-20 object-contain rounded-xl border border-gray-200 bg-gray-50" />
                      <button type="button"
                        onClick={() => { setForm(prev => ({ ...prev, eventLogoBase64: "" })); setLogoPreview(""); }}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white">
                        <X size={11} />
                      </button>
                    </div>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-gray-200 hover:border-supernova-400 rounded-xl px-4 py-3 transition">
                    <Plus size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-500">{form.eventLogoBase64 ? "Cambiar logo" : "Subir logo"}</span>
                    <input type="file" accept="image/*" onChange={handleEventLogoUpload} className="hidden" />
                  </label>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">Se muestra en la página pública del evento.</p>
              </div>

              {/* Tipo de votación */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">Tipo de votación</label>
                <div className="space-y-2">
                  {[
                    { val: "simple", label: "Simple", desc: "Un voto por producto" },
                    { val: "stars",  label: "Estrellas (1–5)", desc: "El usuario califica con estrellas", icon: <Star size={13} className="text-amber-400 fill-amber-400" /> },
                    { val: "points", label: "Por puntos / ranking", desc: "Múltiples votos ordenados con puntos" },
                  ].map(opt => (
                    <label key={opt.val}
                      className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${
                        form.votingType === opt.val
                          ? "border-supernova-500 bg-supernova-50"
                          : "border-gray-100 hover:border-gray-300"
                      }`}
                    >
                      <input type="radio" name="votingType" value={opt.val}
                        checked={form.votingType === opt.val}
                        onChange={() => setForm(prev => {
                          const cur = prev.maxVotesPerDevice;
                          let max = cur;
                          if (opt.val === "points")  max = Math.min(cur, 5);
                          if (opt.val === "stars")   max = cur < 5 ? 10 : cur;
                          if (opt.val === "simple")  max = 1;
                          return {
                            ...prev,
                            votingType: opt.val,
                            maxVotesPerDevice: max,
                            votingPoints: opt.val === "points" ? buildDefaultPoints(max) : prev.votingPoints,
                          };
                        })}
                        className="mt-0.5 accent-supernova-500"
                      />
                      <div>
                        <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                          {opt.icon} {opt.label}
                        </p>
                        <p className="text-xs text-gray-400">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Estrellas max (solo si stars) */}
              {form.votingType === "stars" && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
                    <Star size={12} className="fill-amber-500 text-amber-500" />
                    Los usuarios podrán dar de 1 a 5 estrellas a cada producto.
                    El puntaje promedio se muestra en los resultados.
                  </p>
                </div>
              )}

              {/* Puntos por voto (solo si points) */}
              {form.votingType === "points" && form.maxVotesPerDevice > 1 && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                    <Trophy size={14} className="text-amber-500" /> Puntos por orden de voto
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {form.votingPoints.map((pts, idx) => (
                      <div key={idx} className="flex flex-col items-center gap-1">
                        <span className="text-lg">{RANK_MEDALS[idx]}</span>
                        <span className="text-xs text-gray-500">{idx + 1}Âº voto</span>
                        <input
                          type="number" min="0" max="99" value={pts}
                          onChange={e => {
                            const arr = [...form.votingPoints];
                            arr[idx] = Math.max(0, Number(e.target.value));
                            setForm(prev => ({ ...prev, votingPoints: arr }));
                          }}
                          className="w-14 text-center p-2 border-2 border-gray-200 rounded-lg font-bold text-gray-800 focus:border-supernova-400 focus:outline-none text-sm"
                        />
                        <span className="text-xs text-gray-400">pts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Etiqueta del botón de voto */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">Texto del botón de voto</label>
                <input
                  value={form.voteButtonLabel}
                  onChange={e => setForm(prev => ({ ...prev, voteButtonLabel: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                  placeholder="¡Votar!"
                />
                <p className="text-xs text-gray-400 mt-1">Aparece en el botón de votación en la app.</p>
              </div>

              {/* Sección de patrocinadores */}
              <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                <div>
                  <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                    <Building2 size={14} className="text-purple-500" /> Mostrar sección de patrocinadores
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">Muestra logos de marcas patrocinadoras en la plantilla</p>
                </div>
                <Toggle value={form.showSponsorsSection} onChange={v => setForm(prev => ({ ...prev, showSponsorsSection: v }))} colorOn="bg-purple-500" />
              </div>
            </div>

            {/* â”€â”€ Diseño del evento â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-5">
              <h2 className="font-black text-gray-800 flex items-center gap-2">
                <Sliders size={18} className="text-supernova-500" /> Diseño del evento
              </h2>

              {/* Fondo */}
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-2">Fondo del evento</label>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[
                    { val: "color",    label: "Sólido",   preview: <div className="w-6 h-6 rounded-md border border-gray-200" style={{ backgroundColor: form.backgroundColor }} /> },
                    { val: "gradient", label: "Degradado", preview: <div className="w-6 h-6 rounded-md" style={{ background: `linear-gradient(135deg, ${form.backgroundGradientFrom}, ${form.backgroundGradientTo})` }} /> },
                    { val: "image",    label: "Imagen",   preview: <Image size={20} className="text-gray-400" /> },
                  ].map(opt => (
                    <button key={opt.val} type="button"
                      onClick={() => setForm(prev => ({ ...prev, backgroundType: opt.val }))}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition text-xs font-semibold ${
                        form.backgroundType === opt.val
                          ? "border-supernova-500 bg-supernova-50 text-supernova-700"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      {opt.preview}
                      {opt.label}
                    </button>
                  ))}
                </div>

                {form.backgroundType === "color" && (
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-500 font-medium">Color:</label>
                    <input type="color" value={form.backgroundColor}
                      onChange={e => setForm(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      className="w-12 h-10 border border-gray-200 rounded-lg cursor-pointer p-0.5" />
                    <span className="text-xs font-mono text-gray-600">{form.backgroundColor}</span>
                  </div>
                )}

                {form.backgroundType === "gradient" && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block font-medium">Color inicial</label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={form.backgroundGradientFrom}
                            onChange={e => setForm(prev => ({ ...prev, backgroundGradientFrom: e.target.value }))}
                            className="w-10 h-9 border border-gray-200 rounded cursor-pointer p-0.5" />
                          <span className="text-xs font-mono text-gray-600">{form.backgroundGradientFrom}</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block font-medium">Color final</label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={form.backgroundGradientTo}
                            onChange={e => setForm(prev => ({ ...prev, backgroundGradientTo: e.target.value }))}
                            className="w-10 h-9 border border-gray-200 rounded cursor-pointer p-0.5" />
                          <span className="text-xs font-mono text-gray-600">{form.backgroundGradientTo}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block font-medium">Ángulo: {form.backgroundGradientAngle}Â°</label>
                      <div className="flex items-center gap-2">
                        <input type="range" min="0" max="360" value={form.backgroundGradientAngle}
                          onChange={e => setForm(prev => ({ ...prev, backgroundGradientAngle: Number(e.target.value) }))}
                          className="flex-1 accent-supernova-500" />
                        <div className="w-12 h-8 rounded-lg border border-gray-200 shrink-0" style={{
                          background: `linear-gradient(${form.backgroundGradientAngle}deg, ${form.backgroundGradientFrom}, ${form.backgroundGradientTo})`
                        }} />
                      </div>
                    </div>
                  </div>
                )}

                {form.backgroundType === "image" && (
                  <div className="space-y-3">
                    {(bgPreview || form.backgroundImageBase64) ? (
                      <div className="relative">
                        <img src={bgPreview || imgSrc(form.backgroundImageBase64)} alt="bg" className="w-full h-28 object-cover rounded-xl border" />
                        <button type="button"
                          onClick={() => { setForm(prev => ({ ...prev, backgroundImageBase64: "" })); setBgPreview(""); }}
                          className="absolute top-2 right-2 w-7 h-7 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 cursor-pointer border-2 border-dashed border-gray-200 hover:border-supernova-400 rounded-xl p-4 transition">
                        <Plus size={16} className="text-gray-400" />
                        <span className="text-sm text-gray-500">Subir imagen de fondo</span>
                        <input type="file" accept="image/*" onChange={handleBackgroundImageUpload} className="hidden" />
                      </label>
                    )}
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block font-medium">Ajuste de imagen</label>
                      <div className="flex gap-2">
                        {[
                          { val: "cover",    label: "Cubrir" },
                          { val: "contain",  label: "Contener" },
                          { val: "100% 100%", label: "Estirar" },
                        ].map(opt => (
                          <button key={opt.val} type="button"
                            onClick={() => setForm(prev => ({ ...prev, backgroundImageSize: opt.val }))}
                            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border-2 transition ${
                              form.backgroundImageSize === opt.val
                                ? "border-supernova-500 bg-supernova-50 text-supernova-700"
                                : "border-gray-200 text-gray-500 hover:border-gray-300"
                            }`}
                          >{opt.label}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Estilo del título */}
              <div className="space-y-3 pt-3 border-t border-gray-100">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <Type size={14} className="text-supernova-500" /> Estilo del título
                </label>
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block font-medium">Tipografía</label>
                  <div className="grid grid-cols-2 gap-2">
                    {FONT_OPTIONS.map(f => (
                      <button key={f.val} type="button"
                        onClick={() => setForm(prev => ({ ...prev, titleFont: f.val }))}
                        style={{ fontFamily: f.family }}
                        className={`py-2 px-3 rounded-xl border-2 text-sm transition text-left ${
                          form.titleFont === f.val
                            ? "border-supernova-500 bg-supernova-50 text-supernova-700"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        {f.label} — <span className="opacity-60">Aa</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-end gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block font-medium">Color</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={form.titleColor}
                        onChange={e => setForm(prev => ({ ...prev, titleColor: e.target.value }))}
                        className="w-10 h-9 border border-gray-200 rounded cursor-pointer p-0.5" />
                      <span className="text-xs font-mono text-gray-600">{form.titleColor}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 mb-1 block font-medium">Tamaño</label>
                    <select value={form.titleFontSize}
                      onChange={e => setForm(prev => ({ ...prev, titleFontSize: e.target.value }))}
                      className="w-full p-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400">
                      {Object.entries(FONT_SIZES).map(([k, v]) => (
                        <option key={k} value={k}>{k.toUpperCase()} — {v}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block font-medium">Alineación</label>
                  <div className="flex gap-2">
                    {[
                      { val: "left",   icon: <AlignLeft size={16} /> },
                      { val: "center", icon: <AlignCenter size={16} /> },
                      { val: "right",  icon: <AlignRight size={16} /> },
                    ].map(opt => (
                      <button key={opt.val} type="button"
                        onClick={() => setForm(prev => ({ ...prev, titleAlign: opt.val }))}
                        className={`flex-1 flex items-center justify-center py-2 rounded-xl border-2 transition ${
                          form.titleAlign === opt.val
                            ? "border-supernova-500 bg-supernova-50 text-supernova-700"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        {opt.icon}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Preview inline del título */}
                <div className="rounded-xl p-3 border border-gray-100 bg-gray-50 overflow-hidden">
                  <p style={{
                    color: form.titleColor,
                    fontFamily: FONT_FAMILIES[form.titleFont],
                    fontSize: `calc(${FONT_SIZES[form.titleFontSize]} * 0.6)`,
                    textAlign: form.titleAlign,
                    fontWeight: "bold",
                    lineHeight: 1.2,
                  }}>
                    {form.title || "Título del evento"}
                  </p>
                </div>
              </div>

              {/* Layout de patrocinadores */}
              {form.showSponsorsSection && (
                <div className="space-y-3 pt-3 border-t border-gray-100">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                    <LayoutGrid size={14} className="text-supernova-500" /> Layout de patrocinadores
                  </label>
                  <div className="flex gap-2">
                    {[
                      { val: "static",   label: "Grilla estática" },
                      { val: "carousel", label: "Carrusel" },
                    ].map(opt => (
                      <button key={opt.val} type="button"
                        onClick={() => setForm(prev => ({ ...prev, sponsorsLayout: opt.val }))}
                        className={`flex-1 py-2 rounded-xl text-sm font-semibold border-2 transition ${
                          form.sponsorsLayout === opt.val
                            ? "border-supernova-500 bg-supernova-50 text-supernova-700"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                      >{opt.label}</button>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block font-medium">
                      Logos Bronce por fila: <span className="font-bold text-gray-800">{form.sponsorsPerRow}</span>
                    </label>
                    <input type="range" min="2" max="8" value={form.sponsorsPerRow}
                      onChange={e => setForm(prev => ({ ...prev, sponsorsPerRow: Number(e.target.value) }))}
                      className="w-full accent-supernova-500" />
                    <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                      <span>2</span><span>8</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">Oro = 1 por fila · Plata = 2 por fila · Bronce = configurado arriba</p>
                </div>
              )}
            </div>

            {/* Botón productos en landing */}
            <div className="space-y-3 pt-3 border-t border-gray-100">
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <ShoppingBag size={14} className="text-supernova-500" /> Botón "Ver productos" en el landing
              </label>
              <div className={`flex items-center justify-between rounded-xl px-4 py-3 border transition ${form.showProductsButton ? "bg-supernova-50 border-supernova-200" : "bg-gray-50 border-gray-200"}`}>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Mostrar botón de productos</p>
                  <p className="text-xs text-gray-400 mt-0.5">Aparece debajo del banner del evento en el landing público</p>
                </div>
                <Toggle
                  value={form.showProductsButton}
                  onChange={v => setForm(prev => ({ ...prev, showProductsButton: v }))}
                />
              </div>
              {form.showProductsButton && (
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block font-medium">Texto del botón</label>
                  <input
                    type="text"
                    value={form.productsButtonLabel}
                    onChange={e => setForm(prev => ({ ...prev, productsButtonLabel: e.target.value }))}
                    placeholder="Ver productos participantes"
                    maxLength={120}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-supernova-300"
                  />
                </div>
              )}
            </div>

          </div>

          {/* â”€â”€ Columna derecha: Preview + Patrocinadores & Productos â”€â”€â”€â”€ */}
          <div className="lg:col-span-3 space-y-6">

            {/* Vista previa */}
            <EventPreview form={form} companies={companies} />

            {/* Selector de tiendas */}
            <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-4">
              <h2 className="font-black text-gray-800 flex items-center gap-2">
                <Building2 size={18} className="text-supernova-500" />
                Tiendas participantes
                {selectedCompanyIds.length > 0 && (
                  <span className="ml-auto text-xs bg-supernova-100 text-supernova-700 font-bold px-2 py-0.5 rounded-full">
                    {selectedCompanyIds.length} seleccionada{selectedCompanyIds.length !== 1 ? "s" : ""}
                  </span>
                )}
              </h2>
              <CompanyPicker
                companies={companies}
                selected={selectedCompanyIds}
                onToggle={toggleCompany}
                loadingProducts={loadingProds}
              />
            </div>

            {/* Productos por tienda */}
            {form.sponsors.length > 0 && (
              <div className="space-y-4">
                <h2 className="font-black text-gray-800 flex items-center gap-2 px-1">
                  <PackageSearch size={18} className="text-supernova-500" /> Productos participantes
                </h2>
                {form.sponsors.map(sponsor => {
                  const company = companies.find(c => c.id === sponsor.company);
                  const products = productsByCompany[sponsor.company] || [];
                  return (
                    <SponsorProductSection
                      key={sponsor.company}
                      sponsor={sponsor}
                      company={company}
                      products={products}
                      onToggleProduct={(pid) => toggleProduct(sponsor.company, pid)}
                      onRemoveSponsor={() => removeSponsor(sponsor.company)}
                      onUpdateDisplay={(patch) => updateSponsorDisplay(sponsor.company, patch)}
                    />
                  );
                })}
              </div>
            )}

            {form.sponsors.length === 0 && (
              <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center text-gray-400">
                <PackageSearch size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">Selecciona tiendas arriba para elegir sus productos participantes.</p>
              </div>
            )}
          </div>
        </div>

        {/* â”€â”€ Botón guardar (móvil) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="mt-8 flex justify-end lg:hidden">
          <button type="submit" disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-supernova-500 hover:bg-supernova-600 text-white py-3.5 rounded-2xl font-bold transition disabled:opacity-60 text-base">
            {saving ? <Loader2 size={18} className="animate-spin" /> : null}
            {saving ? "Guardando…" : isEditing ? "Actualizar evento" : "Crear evento"}
          </button>
        </div>
      </form>
    </div>
  );
}
